<!-- LoginForm.vue -->
<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useUserStore } from "@/stores/useUserStore";
import { useSettingStore } from "@/stores/useSettingStore";

import ReactivationModal from "./ReactivationModal.vue";

import { GoogleSignInButton } from "vue3-google-signin";
import UserRegistrationForm from "./UserRegistrationForm.vue";
import UserRegistrationFormMobile from "./UserRegistrationFormMobile.vue";
import ForgotPasswordModal from "./ForgotPasswordModal.vue";
import ResetPasswordModal from "./ResetPasswordModal.vue";
import WelcomeMobileModal from "./WelcomeMobileModal.vue";

import UpgradePage from "@/components/User/UpgradePage.vue";
import ManagedAccountOnboarding from "@/components/ManagedAccount/ManagedAccountOnboarding.vue";

import LoginContextModal from "@/components/User/LoginContextModal.vue";

import { useOnboardingStore } from "@/stores/useOnboardingStore";

import { resolveIsNewAccount, trackCompleteRegistrationGoogle } from "@/utils/tracking";

const userStore = useUserStore();
const settingStore = useSettingStore();
console.log("settingStore");
const onboardingStore = useOnboardingStore();

const router = useRouter();
const route = useRoute();

const isPasswordVisible = ref(false);
const isSubmitting = ref(false);

// Heurística: clique no iframe do Google tira o foco da janela (blur) e o
// activeElement passa a ser o iframe. Não há evento de clique direto disponível.
const isGooglePopupPending = ref(false);

const email = ref("");
const password = ref("");
const rememberMe = ref(false);
const showRegistrationModal = ref(false);
const showRegistrationMobileModal = ref(false);
const pendingIsSchoolSignup = ref(false);
const showWelcomeMobileModal = ref(false);

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
  navigator.userAgent
);

// Bypass de mobile para a conta de sistema: "Sys" segue o mesmo fluxo do
// desktop no celular, em vez de parar na tela de indisponibilidade.
function isSysUser() {
  return userStore.currentUser?.userName === "Sys";
}

const passwordInputRef = ref(null);

const showSuccessToast = ref(false);
const showErrorToast = ref(false);
const errorMessageToast = ref("");

const showForgotPasswordModal = ref(false);
const showResetPasswordModal = ref(false);
const resetEmail = ref("");
// E-mail vindo do cadastro ("já tem conta" -> "Esqueci minha senha").
const forgotInitialEmail = ref("");

// Atalhos do cadastro quando o e-mail já tem dono: volta ao login com o
// e-mail preenchido, ou abre o "Esqueci minha senha" já com ele.
function handleRegistrationLoginWith(prefilledEmail) {
  handleCloseModals();
  email.value = prefilledEmail || "";
  nextTick(() => passwordInputRef.value?.focus());
}
function handleRegistrationForgotPassword(prefilledEmail) {
  handleCloseModals();
  forgotInitialEmail.value = prefilledEmail || "";
  showForgotPasswordModal.value = true;
}

const googleWrap = ref(null);
const googleBtnWidth = ref(0);

// No mobile trocamos o popup pelo redirect: no Safari do iPhone o popup abre,
// o usuário escolhe a conta e a janela morre em accounts.google.com/gsi/transform
// sem devolver a credencial. Sem popup não há opener para o Safari cortar.
// O desktop segue no popup, que funciona e não tira a pessoa da página.
const googleUxMode = isMobile ? "redirect" : "popup";

// Para onde o Google posta a credencial no modo redirect. Precisa ser a MESMA
// origem da página, não a da API: o g_csrf_token é gravado como cookie host-only
// no host do SPA, então um POST para outro host chegaria sem ele e o backend
// reprovaria o login. Quem leva daqui até a API é a Pages Function em
// functions/api/auth/google/redirect.js; no ambiente local, o proxy do devServer.
// Cada origem por onde se entra no site (www, sem www e localhost) precisa da sua
// própria "Authorized redirect URI" cadastrada no Google Cloud Console.
const googleLoginUri = `${window.location.origin}/api/auth/google/redirect`;

const googleRedirectErrors = {
  csrf: "A verificação de segurança do login com o Google falhou. Tente novamente.",
  missing_credential: "O Google não enviou a credencial. Tente novamente.",
  invalid_token: "A credencial do Google é inválida ou expirou. Tente novamente.",
  deactivated: "Sua conta está desativada.",
  failed: "Não foi possível concluir o login com o Google.",
  unexpected: "Ocorreu um erro inesperado no login com o Google.",
};

const showReactivationModal = ref(false);
const deactivationDate = ref("");

const showUpgradeModal = ref(false);
const showManagedOnboarding = ref(false);

const showContextModal = ref(false);
// Origem do login que disparou a seleção de contexto — usado só para saber se
// deve disparar o pixel de conversão do Google em handleContextDone().
const pendingContextLoginSource = ref(null); // "google" | "password" | null
// Payload do login Google que ficou aguardando a seleção de contexto. É dele que
// trackCompleteRegistrationGoogle lê o indicador de conta recém-criada.
const pendingContextGooglePayload = ref(null);

// `loginPayload` só é informado nos fluxos que podem criar a conta no próprio
// login (Google). No mobile, conta recém-criada termina na tela de boas-vindas,
// como no cadastro por formulário; quem já era cadastrado segue para o aviso de
// indisponibilidade. resolveIsNewAccount devolve undefined quando o backend não
// informa o indicador — nesse caso NÃO tratamos como cadastro (mesma doutrina
// de trackCompleteRegistrationGoogle).
function redirectAfterLogin(loginPayload) {
  if (isMobile && !isSysUser()) {
    if (resolveIsNewAccount(loginPayload) === true) {
      showWelcomeMobileModal.value = true;
      return;
    }
    router.push({ name: "MobileBlocked" });
    return;
  }
  const user = userStore.currentUser;
  // A estante e a casa do usuario logado: e a lista de textos dele, que e o que
  // ele veio fazer. O painel geral continua no menu, como visao de conjunto.
  router.push(user?.onboardingComplete ? { name: "TextCardsForm" } : { name: "FirstAccessView" });
}

function togglePasswordVisibility() {
  isPasswordVisible.value = !isPasswordVisible.value;

  nextTick(() => {
    passwordInputRef.value?.focus();
  });
}

function dismissToast() {
  showSuccessToast.value = false;
  showErrorToast.value = false;
  errorMessageToast.value = "";
}

function handleCloseModals() {
  showForgotPasswordModal.value = false;
  showResetPasswordModal.value = false;
  showRegistrationModal.value = false;
  showRegistrationMobileModal.value = false;
  showReactivationModal.value = false;
  dismissToast();
  userStore.clearStoreError();
}

function measureGoogleWidth() {
  if (googleWrap.value) {
    // O GIS ignora larguras acima de 400px. Medir sem limitar faria o botão
    // renderizar mais estreito que o contêiner sem motivo aparente.
    googleBtnWidth.value = Math.min(Math.round(googleWrap.value.clientWidth), 400);
  }
}

function handleWindowBlur() {
  if (googleWrap.value?.contains(document.activeElement)) {
    isGooglePopupPending.value = true;
  }
}

function handleWindowFocus() {
  // Janela recuperou o foco sem credencial: popup fechado/cancelado.
  isGooglePopupPending.value = false;
}

// Retorno do fluxo redirect: o backend já autenticou e nos devolveu à /login
// com um código de uso único (ou com um erro). Daqui para frente o tratamento
// é o mesmo do popup — inclusive a seleção de contexto.
async function handleGoogleRedirectReturn() {
  const errorCode = route.query.googleError;
  const code = route.query.googleCode;

  if (!errorCode && !code) return;

  // Limpa a querystring antes de qualquer await: o código é de uso único e não
  // deve sobreviver a um F5, nem ficar no histórico depois de consumido.
  router.replace({ name: "LoginView", query: {} });

  if (errorCode) {
    userStore.error =
      googleRedirectErrors[errorCode] || googleRedirectErrors.unexpected;
    return;
  }

  isSubmitting.value = true;

  try {
    const data = await userStore.exchangeGoogleRedirectCode(code);

    if (data?.requiresContextSelection === true) {
      pendingContextLoginSource.value = "google";
      pendingContextGooglePayload.value = data;
      showContextModal.value = true;
      return;
    }

    trackCompleteRegistrationGoogle(data);

    try {
      await settingStore.fetchSetting();
    } catch (err) {
      console.warn("⚠️ Erro ao carregar settings após login Google:", err.message);
    }

    redirectAfterLogin(data);
  } catch (err) {
    console.error("Falha ao resgatar o login do Google:", err);
  } finally {
    isSubmitting.value = false;
  }
}

onMounted(async () => {
  measureGoogleWidth();
  window.addEventListener("resize", measureGoogleWidth);
  window.addEventListener("blur", handleWindowBlur);
  window.addEventListener("focus", handleWindowFocus);

  userStore.clearStoreError();

  await handleGoogleRedirectReturn();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", measureGoogleWidth);
  window.removeEventListener("blur", handleWindowBlur);
  window.removeEventListener("focus", handleWindowFocus);
});

async function handleLogin() {
  if (isSubmitting.value || isGooglePopupPending.value) return;
  isSubmitting.value = true;

  try {
    userStore.error = null;

    const data = await userStore.login({
      email: email.value,
      password: password.value,
      rememberMe: rememberMe.value,
    });

    if (isMobile && !isSysUser()) {
      redirectAfterLogin();
      return;
    }

    if (data?.requiresContextSelection === true) {
      pendingContextLoginSource.value = "password";
      showContextModal.value = true;
      return;
    }

    if (userStore.currentUser && userStore.currentUser.status !== 1) {
      const status = userStore.currentUser.status;

      if (status === 0) {
        userStore.error =
          "Sua conta está pendente de ativação. Verifique seu e-mail.";
      } else if (status === 3) {
        userStore.error = "Esta conta foi bloqueada.";
      }

      userStore.logout();
      return;
    }

    await settingStore.fetchSetting();
    redirectAfterLogin();
  } catch (error) {
    if (error.requiresReactivation) {
      deactivationDate.value = error.deactivatedAt
        ? new Date(error.deactivatedAt).toLocaleDateString()
        : "data recente";

      showReactivationModal.value = true;
      userStore.error = null;
    }
  } finally {
    isSubmitting.value = false;
  }
}

async function handleContextDone() {
  showContextModal.value = false;

  if (userStore.currentUser && userStore.currentUser.status !== 1) {
    pendingContextLoginSource.value = null;
    pendingContextGooglePayload.value = null;
    userStore.logout();
    return;
  }

  // Guardado antes da limpeza dos refs porque o redirect ainda precisa dele
  // para saber se a conta nasceu neste login.
  const googlePayload =
    pendingContextLoginSource.value === "google"
      ? pendingContextGooglePayload.value
      : null;

  if (googlePayload) {
    trackCompleteRegistrationGoogle(googlePayload);
  }
  pendingContextLoginSource.value = null;
  pendingContextGooglePayload.value = null;

  try {
    await settingStore.fetchSetting();
  } catch (err) {
    console.warn(
      "⚠️ Erro ao carregar settings após seleção de contexto:",
      err.message
    );
  }

  redirectAfterLogin(googlePayload);
}

function handleContextCancel() {
  showContextModal.value = false;
  pendingContextLoginSource.value = null;
  pendingContextGooglePayload.value = null;
}

async function handleConfirmReactivation(callback) {
  try {
    await userStore.reactivateAccount({
      email: email.value,
      password: password.value,
    });

    await callback(true);

    await handleLogin();
    showReactivationModal.value = false;
  } catch (err) {
    callback(false);
    showErrorToast("Erro ao reativar conta.");
  }
}

const handleLoginSuccess = async (response) => {
  isGooglePopupPending.value = false;

  // Login em andamento: descarta a credencial e deixa o fluxo corrente decidir
  // o destino, para não competir com ele.
  if (isSubmitting.value) {
    console.warn("Credencial do Google ignorada: login em andamento.");
    return;
  }

  // Já existe sessão — seja um popup órfão concluído após login por senha, seja
  // alguém que voltou para /login já autenticado (possível no mobile, onde o
  // guard do router não expulsa usuário logado desta rota). Em nenhum dos dois
  // trocamos a sessão ativa. Mas também não paramos em silêncio: a pessoa tocou
  // no botão e escolheu uma conta, então precisa ir para algum lugar em vez de
  // ficar olhando uma tela que não reage.
  if (userStore.currentUser) {
    console.warn("Credencial do Google ignorada: já existe sessão ativa.");
    redirectAfterLogin();
    return;
  }

  const idToken = response?.credential;

  if (!idToken) {
    userStore.error = "Não foi possível obter o token do Google.";
    return;
  }

  isSubmitting.value = true;

  try {
    userStore.error = null;
    const data = await userStore.loginWithGoogle(idToken);

    if (data?.requiresContextSelection === true) {
      pendingContextLoginSource.value = "google";
      pendingContextGooglePayload.value = data;
      showContextModal.value = true;
      return;
    }

    // Só conta como cadastro se o backend indicar que a conta nasceu agora.
    trackCompleteRegistrationGoogle(data);

    try {
      await settingStore.fetchSetting();
      console.log("✅ Settings carregadas após login Google");
    } catch (err) {
      console.warn(
        "⚠️ Erro ao carregar settings após login Google:",
        err.message
      );
    }

    redirectAfterLogin(data);
  } catch (error) {
    console.error("Falha ao processar o token no backend:", error);
    userStore.error = "Falha ao autenticar com o Google.";
  } finally {
    isSubmitting.value = false;
  }
};

const handleLoginError = () => {
  isGooglePopupPending.value = false;
  console.error("O login com o Google falhou.");
  userStore.error = "O login com o Google não pôde ser concluído.";
};

function handleOpenForgotPasswordModal() {
  dismissToast();
  forgotInitialEmail.value = "";
  showForgotPasswordModal.value = true;
}

function handleOpenRegistrationModal(isSchoolSignup = false) {
  dismissToast();
  pendingIsSchoolSignup.value = isSchoolSignup;
  if (isMobile) {
    showRegistrationMobileModal.value = true;
  } else {
    showRegistrationModal.value = true;
  }
}

function handleForgotPasswordSuccess(email) {
  resetEmail.value = email;
  showForgotPasswordModal.value = false;
  showResetPasswordModal.value = true;
  dismissToast();
}

function handleResetPasswordSuccess(userEmail) {
  showResetPasswordModal.value = false;

  email.value = userEmail;
  password.value = "";

  showSuccessToast.value = true;
  showErrorToast.value = false;
  errorMessageToast.value = "";

  nextTick(() => {
    passwordInputRef.value?.focus();

    setTimeout(dismissToast, 5000);
  });
}

function handleResetPasswordError(message) {
  errorMessageToast.value = message;
  showErrorToast.value = true;
  showSuccessToast.value = false;

  nextTick(() => {
    setTimeout(dismissToast, 5000);
  });
}

function handleRegistrationVerified() {
  showRegistrationModal.value = false;
  showUpgradeModal.value = true;
}

function handleManagedAccountsNeeded() {
  console.log("[LoginForm] handleManagedAccountsNeeded chamado");

  showUpgradeModal.value = false;
  showManagedOnboarding.value = true;

  console.log(
    "[LoginForm] showManagedOnboarding:",
    showManagedOnboarding.value
  );
}

function handleUpgradeClose() {
  showUpgradeModal.value = false;
  onboardingStore.reset();
}

function handleOnboardingDone() {
  showManagedOnboarding.value = false;
  onboardingStore.reset();
  redirectAfterLogin();
}
</script>

<template>
  <div class="page-container">
    <!-- TOAST FLUTUANTE (sucesso/erro redefinição) -->
    <transition name="toast-fade">
      <div
        v-if="showSuccessToast || showErrorToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            showErrorToast ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />

              <path
                v-if="showErrorToast"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />

              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ showErrorToast ? "Erro" : "Sucesso!" }}
            </p>

            <p class="toast-message">
              {{
                showErrorToast
                  ? errorMessageToast
                  : "Senha redefinida com sucesso! Você já pode fazer login com sua nova senha."
              }}
            </p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="dismissToast"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>

    <div class="flex justify-center w-full mb-4">
      <span class="flex items-center gap-1">
        <img
          src="/litterae_pena_fundo_transparente.png"
          alt="Logo Litterae"
          class="w-10 h-10 object-contain"
        />

        <span class="font-bold text-4xl text-slate-900">Litterae</span>
      </span>
    </div>

    <div class="login-panel">
      <div class="login-body">
        <form class="space-y-5" @submit.prevent="handleLogin">
          <div class="form-group">
            <label for="email" class="form-label">Seu e-mail</label>

            <input
              type="email"
              id="email"
              v-model="email"
              class="form-input"
              placeholder="nome@dominio.com"
              required
            />
          </div>

          <div class="form-group">
            <label for="password" class="form-label">Senha</label>

            <div class="password-input-wrapper">
              <input
                ref="passwordInputRef"
                :type="isPasswordVisible ? 'text' : 'password'"
                id="password"
                v-model="password"
                placeholder=""
                class="form-input"
                required
              />

              <button
                type="button"
                @click="togglePasswordVisibility"
                class="password-toggle-btn"
                aria-label="Alternar visibilidade da senha"
              >
                <svg
                  v-if="isPasswordVisible"
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />

                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>

                <svg
                  v-else
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7 1.274-4.057 5.064-7 9.542-7 1.422 0 2.774.376 3.982 1.025M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />

                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17.938 17.938l-12-12"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <input
                id="remember"
                type="checkbox"
                v-model="rememberMe"
                class="remember-checkbox"
              />

              <label for="remember" class="remember-label">Lembre-me</label>
            </div>

            <a
              href="#"
              @click.prevent="handleOpenForgotPasswordModal"
              class="forgot-password-link"
            >
              <b>Esqueceu a senha?</b>
            </a>
          </div>

          <p v-if="userStore.error" class="error-message">
            {{ userStore.error }}
          </p>

          <button
            type="submit"
            class="btn btn-lg btn-primary w-full"
            :disabled="isSubmitting || isGooglePopupPending"
          >
            {{ isSubmitting ? "Entrando..." : "Entrar" }}
          </button>

          <div class="separator">ou</div>

          <div
            ref="googleWrap"
            class="google-login-wrapper"
            :class="{ 'google-login-disabled': isSubmitting }"
          >
            <GoogleSignInButton
              :size="'large'"
              :type="'standard'"
              :theme="'outline'"
              :text="'signin_with'"
              :logo-alignment="'center'"
              :width="googleBtnWidth"
              :auto-select="false"
              :ux-mode="googleUxMode"
              :login-uri="googleLoginUri"
              @success="handleLoginSuccess"
              @error="handleLoginError"
            />
          </div>

          <p class="signup-text">Não tem uma conta ainda? Cadastre-se como:</p>

          <div class="signup-choice">
            <a
              href="#"
              @click.prevent="handleOpenRegistrationModal(false)"
              class="signup-choice-btn"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="8" r="3.2" />
                <path d="M5 20c0-3.5 3.1-6 7-6s7 2.5 7 6" />
              </svg>
              <span>Sou uma pessoa</span>
            </a>
            <a
              href="#"
              @click.prevent="handleOpenRegistrationModal(true)"
              class="signup-choice-btn"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M4 21V9l8-5 8 5v12" />
                <path d="M9 21v-6h6v6" />
                <path d="M9 12h.01M15 12h.01M9 8h.01M15 8h.01" />
              </svg>
              <span>Sou uma escola / instituição</span>
            </a>
          </div>
        </form>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <UserRegistrationForm
      v-if="showRegistrationModal"
      :is-school-signup="pendingIsSchoolSignup"
      @close="handleCloseModals"
      @verified="handleRegistrationVerified"
      @login-with="handleRegistrationLoginWith"
      @forgot-password="handleRegistrationForgotPassword"
    />

    <UserRegistrationFormMobile
      v-if="showRegistrationMobileModal"
      :is-school-signup="pendingIsSchoolSignup"
      @close="handleCloseModals"
      @login-with="handleRegistrationLoginWith"
      @forgot-password="handleRegistrationForgotPassword"
    />

    <!-- Cadastro via Google concluído no celular. Não é fechável: a única saída
         é o próprio "Acessar a página inicial" do modal. -->
    <WelcomeMobileModal v-if="showWelcomeMobileModal" />

    <ForgotPasswordModal
      v-if="showForgotPasswordModal"
      :initial-email="forgotInitialEmail"
      @close="handleCloseModals"
      @success="handleForgotPasswordSuccess"
    />

    <ResetPasswordModal
      v-if="showResetPasswordModal"
      :email="resetEmail"
      @close="handleCloseModals"
      @login="handleResetPasswordSuccess"
      @error="handleResetPasswordError"
    />

    <ReactivationModal
      v-if="showReactivationModal"
      :deactivationDate="deactivationDate"
      @close="handleCloseModals"
      @confirm="handleConfirmReactivation"
    />

    <LoginContextModal
      v-if="showContextModal"
      @done="handleContextDone"
      @cancel="handleContextCancel"
    />

    <UpgradePage
      v-if="showUpgradeModal"
      :usage-type-filter="onboardingStore.usageType"
      :show-free-continue-btn="onboardingStore.isIndividual"
      @close="handleUpgradeClose"
      @managed-accounts-needed="handleManagedAccountsNeeded"
    />

    <ManagedAccountOnboarding
      v-if="showManagedOnboarding"
      @done="handleOnboardingDone"
    />
  </Teleport>
</template>

<style scoped>

/* =========================
   PAGE / CARD
========================= */
.page-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 100vh;
  padding: 5rem 2rem 2rem;
  background: var(--login-gradient);
}

.login-panel {
  width: 100%;
  max-width: 28rem;
  background-color: var(--bg-content);
  border-radius: 20px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.2);
  overflow: hidden;
  margin-top: 0.5rem;
}

.login-body {
  padding: 1.75rem 1.75rem 1.9rem;
}

/* =========================
   REMEMBER + LINK
========================= */
.remember-checkbox {
  width: 1rem;
  height: 1rem;
  border-radius: 4px;
  border-color: var(--border-color);
  cursor: pointer;
}

.remember-label {
  font-size: 0.875rem;
  color: var(--text-light);
}

.forgot-password-link {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--focus-border);
  text-decoration: none;
  transition: color 0.2s, text-decoration 0.2s;
}

.forgot-password-link:hover {
  color: var(--brand);
  text-decoration: underline;
}

/* =========================
   ERRO STORE
========================= */
.error-message {
  color: var(--danger);
  font-size: 0.875rem;
  text-align: center;
}

/* =========================
   SIGNUP
========================= */
.signup-text {
  padding-top: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-light);
  text-align: center;
}

.signup-link {
  font-weight: 500;
  color: var(--focus-border);
  text-decoration: none;
  transition: color 0.2s, text-decoration 0.2s;
}

.signup-link:hover {
  color: var(--brand);
  text-decoration: underline;
}

.signup-choice {
  display: flex;
  gap: 0.6rem;
  padding-top: 0.35rem;
}

.signup-choice-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  padding: 0.75rem 0.5rem;
  border: 1.5px solid var(--border-color);
  border-radius: 12px;
  color: var(--text-dark);
  font-size: 0.78rem;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.signup-choice-btn svg {
  color: var(--text-light);
}

.signup-choice-btn:hover {
  border-color: var(--brand);
  box-shadow: var(--focus-ring-brand);
}


.btn:hover:not(:disabled) {
  /* hover genérico sem transform e sem box-shadow */
}

/* =========================
   GOOGLE LOGIN BUTTON
   Botão renderizado pelo próprio Google Identity Services, visível.

   Antes havia um botão nosso por cima de um iframe transparente do GIS. Esse
   arranjo quebrava no WebKit (iOS): o toque não era honrado e nada acontecia.
   Pior, quando o script do Google não carregava o usuário via um botão
   perfeito e inerte, porque o visual era desenhado por nós e independia do
   GIS. Renderizar o botão real custa o visual padronizado do Google, mas o
   clique passa a nascer no elemento do próprio Google e a falha fica visível.
========================= */
.google-login-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  min-height: 40px;
}

.google-login-disabled {
  pointer-events: none;
  opacity: 0.6;
  cursor: not-allowed;
}

/* Negrito no rótulo do botão do Google.
   O GIS não expõe controle de tipografia: renderButton só aceita type, theme,
   size, text, shape, logo_alignment, width e locale. Então a única via é
   alcançar o texto por CSS, e isso depende de como o Google monta o botão.
   Se ele renderizar DOM comum, as regras abaixo pegam. Se servir o botão
   dentro de um <iframe> de accounts.google.com, nada nosso atravessa a
   fronteira cross-origin e estas regras simplesmente não casam com nada —
   inócuas, nunca quebram o botão. */
.google-login-wrapper :deep([role="button"]),
.google-login-wrapper :deep(button),
.google-login-wrapper :deep(span) {
  font-weight: 700 !important;
}

/* =========================
   SEPARADOR
========================= */
.separator {
  display: flex;
  align-items: center;
  text-align: center;
  color: var(--text-light);
  font-size: 0.875rem;
  margin: 0.5rem 0;
}

.separator::before,
.separator::after {
  content: "";
  flex: 1;
  border-bottom: 1px solid var(--border-color);
}

.separator:not(:empty)::before {
  margin-right: 1em;
}

.separator:not(:empty)::after {
  margin-left: 1em;
}

/* =========================
   PASSWORD TOGGLE
========================= */
.password-input-wrapper {
  position: relative;
}

.password-input-wrapper .form-input {
  padding-right: 3rem;
}

.password-toggle-btn {
  position: absolute;
  top: 50%;
  right: 0.75rem;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: var(--text-light);
  transition: color 0.2s;
  font-size: 1rem;
  line-height: 1;
  z-index: 10;
}

.password-toggle-btn:hover {
  color: var(--focus-border);
}

.text-center {
  text-align: center;
}

@media (max-width: 640px) {
  .page-container {
    padding: 2rem 1rem 2rem;
  }

  .login-panel {
    border-radius: 16px;
  }

  .login-body {
    padding: 1.25rem 1.25rem 1.5rem;
  }
}
</style>